/* eslint-disable react-hooks/set-state-in-effect, react-hooks/preserve-manual-memoization */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Eye, FileText, DoorOpen } from 'lucide-react';
import logger from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending: { label: 'In Attesa', className: 'bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]', color: '#F59E0B' },
  confirmed: { label: 'Confermato', className: 'bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))]', color: '#1E7A58' },
  rejected: { label: 'Rifiutato', className: 'bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))]', color: '#E5484D' },
  cancelled: { label: 'Annullato', className: 'bg-slate-500 text-white', color: '#64748B' },
  completed: { label: 'Completato', className: 'bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))]', color: '#1677C7' },
};

export default function MieiAppuntamentiMedico() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailAppt, setDetailAppt] = useState(null);
  const [noteText, setNoteText] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/doctor/appointments`, {
        params: { status: statusFilter !== 'all' ? statusFilter : undefined },
      });
      setAppointments(res.data);
    } catch (err) {
      logger.error('Fetch error:', err);
      toast.error('Errore nel caricamento degli appuntamenti');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/doctor/appointments/${id}/status`, { status });
      toast.success(`Stato aggiornato: ${STATUS_MAP[status]?.label}`);
      setDetailAppt(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const saveNotes = async (id) => {
    try {
      await axios.put(`${API}/doctor/appointments/${id}/notes`, { note_operatore: noteText });
      toast.success('Note salvate');
      fetchData();
    } catch { toast.error('Errore nel salvataggio'); }
  };

  const apptStudioLabel = (a) => {
    if (!a.studio_id) return null;
    return a.studio_numero ? `Studio ${a.studio_numero} - ${a.studio_nome}` : a.studio_nome;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1F2A37]" style={{ fontFamily: 'Space Grotesk' }}>I miei appuntamenti</h2>
          <p className="text-muted-foreground">Gestisci i tuoi appuntamenti: conferma, rifiuta, completa e aggiungi note.</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="doctor-status-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="pending">In Attesa</SelectItem>
            <SelectItem value="confirmed">Confermati</SelectItem>
            <SelectItem value="completed">Completati</SelectItem>
            <SelectItem value="rejected">Rifiutati</SelectItem>
            <SelectItem value="cancelled">Annullati</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 rounded" />)}</div>
          ) : appointments.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground" data-testid="doctor-appts-empty">Nessun appuntamento trovato</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Paziente</TableHead>
                    <TableHead>Servizio</TableHead>
                    <TableHead>Studio</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Ora</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appointments.map(a => (
                    <TableRow key={a.id} data-testid="doctor-appointment-row">
                      <TableCell className="font-medium">{a.patient_nome} {a.patient_cognome}</TableCell>
                      <TableCell>{a.service_nome}</TableCell>
                      <TableCell>
                        {a.studio_id
                          ? <Badge variant="outline" className="text-[11px]">{a.studio_numero ? `Studio ${a.studio_numero}` : a.studio_nome}</Badge>
                          : <span className="text-muted-foreground text-xs">—</span>}
                      </TableCell>
                      <TableCell>{a.data}</TableCell>
                      <TableCell>{a.ora}</TableCell>
                      <TableCell><Badge className={STATUS_MAP[a.status]?.className}>{STATUS_MAP[a.status]?.label}</Badge></TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {a.status === 'pending' && (
                            <>
                              <Button size="sm" variant="ghost" className="text-green-600 h-8 px-2" onClick={() => updateStatus(a.id, 'confirmed')} title="Conferma"><CheckCircle size={16} /></Button>
                              <Button size="sm" variant="ghost" className="text-red-600 h-8 px-2" onClick={() => updateStatus(a.id, 'rejected')} title="Rifiuta"><XCircle size={16} /></Button>
                            </>
                          )}
                          {a.status === 'confirmed' && (
                            <Button size="sm" variant="ghost" className="text-blue-600 h-8 px-2" onClick={() => updateStatus(a.id, 'completed')} title="Completa"><CheckCircle size={16} /></Button>
                          )}
                          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => { setDetailAppt(a); setNoteText(a.note_operatore || ''); }} title="Dettagli"><Eye size={16} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!detailAppt} onOpenChange={() => setDetailAppt(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Dettaglio Appuntamento</DialogTitle>
          </DialogHeader>
          {detailAppt && (
            <div className="space-y-4">
              <div className="bg-[hsl(var(--secondary))] rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Paziente</span><span className="font-medium">{detailAppt.patient_nome} {detailAppt.patient_cognome}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{detailAppt.patient_email}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Telefono</span><span>{detailAppt.patient_telefono || 'N/D'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Servizio</span><span className="font-medium">{detailAppt.service_nome}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Data e Ora</span><span className="font-medium">{detailAppt.data} alle {detailAppt.ora}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Durata</span><span>{detailAppt.durata_minuti} min</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1"><DoorOpen size={13} /> Studio</span><span className="font-medium">{apptStudioLabel(detailAppt) || <span className="text-muted-foreground font-normal">Non assegnato</span>}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Stato</span><Badge className={STATUS_MAP[detailAppt.status]?.className}>{STATUS_MAP[detailAppt.status]?.label}</Badge></div>
                {detailAppt.note_paziente && (
                  <div className="pt-2 border-t"><span className="text-muted-foreground">Note paziente:</span><p className="mt-1">{detailAppt.note_paziente}</p></div>
                )}
              </div>
              <div>
                <Label>Note (visibili allo staff)</Label>
                <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Aggiungi note..." className="mt-1" data-testid="doctor-notes-textarea" />
                <Button size="sm" onClick={() => saveNotes(detailAppt.id)} className="mt-2 bg-[#238A92] hover:bg-[#0F5E66] text-white">
                  <FileText size={14} className="mr-1" /> Salva Note
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            {detailAppt?.status === 'pending' && (
              <div className="flex gap-2">
                <Button onClick={() => updateStatus(detailAppt.id, 'confirmed')} className="bg-green-600 hover:bg-green-700 text-white"><CheckCircle size={14} className="mr-1" /> Conferma</Button>
                <Button onClick={() => updateStatus(detailAppt.id, 'rejected')} variant="destructive"><XCircle size={14} className="mr-1" /> Rifiuta</Button>
              </div>
            )}
            {detailAppt?.status === 'confirmed' && (
              <Button onClick={() => updateStatus(detailAppt.id, 'completed')} className="bg-blue-600 hover:bg-blue-700 text-white"><CheckCircle size={14} className="mr-1" /> Completa</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
